import { API_URL } from '../config/api';
import { inferAttachmentFileType, uploadFilesWithProgress } from '../utils/uploadFilesWithProgress';

const DB_NAME = 'workloop_offline_attachments';
const STORE_NAME = 'files';
const DB_VERSION = 1;

const openDb = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    reject(new Error('IndexedDB is not available'));
    return;
  }
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME, { keyPath: 'id' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const withStore = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const request = fn(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const isInlineFileSrc = (value) => {
  const src = String(value || '');
  return src.startsWith('blob:') || src.startsWith('data:');
};

export const isLocalAttachmentKey = (value) => String(value || '').startsWith('local-att-');

export const isPendingLocalAttachment = (attachment) => {
  if (!attachment) return false;
  if (attachment.pendingUpload) return true;
  const key = attachment.localFileId || attachment.fileUrl || attachment.id;
  return isLocalAttachmentKey(key) || isInlineFileSrc(attachment.fileUrl);
};

export const shouldUseOfflineAttachment = (error) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (!error) return true;
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') return true;
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return true;
  const status = error?.response?.status;
  if (!error.response) return true;
  return status >= 500;
};

const localPreviewUrlCache = new Map();

export const resolveLocalPreviewUrl = async (fileKey) => {
  if (isInlineFileSrc(fileKey)) return fileKey;
  const id = String(fileKey || '');
  if (!isLocalAttachmentKey(id)) return null;
  if (localPreviewUrlCache.has(id)) return localPreviewUrlCache.get(id);
  const stored = await readLocalFile(id);
  if (!stored?.blob) return null;
  const url = URL.createObjectURL(stored.blob);
  localPreviewUrlCache.set(id, url);
  return url;
};

export const toServerAttachments = (attachments = []) =>
  (attachments || [])
    .filter((att) => (
      att?.fileUrl
      && !isPendingLocalAttachment(att)
      && !isInlineFileSrc(att.fileUrl)
      && !isLocalAttachmentKey(att.fileUrl)
    ))
    .map((att) => {
      const next = {
        fileName: att.fileName,
        fileUrl: att.fileUrl,
        fileType: att.fileType,
        fileSize: att.fileSize,
      };
      if (
        att.previousFileUrl
        && !isInlineFileSrc(att.previousFileUrl)
        && !isLocalAttachmentKey(att.previousFileUrl)
      ) {
        next.previousFileUrl = att.previousFileUrl;
        if (att.previousFileName) next.previousFileName = att.previousFileName;
      }
      if (Array.isArray(att.vaultContents) && att.vaultContents.length) {
        next.vaultContents = att.vaultContents;
      }
      return next;
    });

const newLocalFileId = () => `local-att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const stashLocalFile = async (file) => {
  const id = newLocalFileId();
  await withStore('readwrite', (store) => store.put({
    id,
    blob: file,
    name: file.name,
    type: file.type,
    size: file.size,
    savedAt: Date.now(),
  }));
  return id;
};

export const readLocalFile = async (id) => {
  if (!id) return null;
  try {
    return await withStore('readonly', (store) => store.get(id));
  } catch {
    return null;
  }
};

export const deleteLocalFile = async (id) => {
  if (!id) return;
  try {
    await withStore('readwrite', (store) => store.delete(id));
  } catch {
    // ignore
  }
};

export const fileToLocalAttachment = async (file, fileType) => {
  const localFileId = await stashLocalFile(file);
  const type = inferAttachmentFileType({
    fileName: file.name,
    fileType: fileType || ((file.type || '').startsWith('image/') ? 'IMAGE' : 'FILE'),
  });
  const previewUrl = type === 'IMAGE' ? URL.createObjectURL(file) : '';
  return {
    id: localFileId,
    localFileId,
    pendingUpload: true,
    fileName: file.name || 'file',
    fileType: type,
    fileSize: file.size || 0,
    fileUrl: previewUrl || localFileId,
  };
};

export const filesToLocalAttachments = async (files = []) => {
  const attachments = [];
  for (const item of files) {
    const file = item?.file instanceof Blob ? item.file : item;
    if (!(file instanceof Blob)) continue;
    const local = await fileToLocalAttachment(file, item?.fileType);
    if (Array.isArray(item?.vaultContents) && item.vaultContents.length) {
      local.vaultContents = item.vaultContents;
    }
    attachments.push(local);
  }
  return attachments;
};

export const attachmentsForQueue = (attachments = []) =>
  (attachments || []).map((att) => {
    if (!isPendingLocalAttachment(att)) return att;
    return {
      id: att.localFileId || att.id,
      localFileId: att.localFileId || att.id,
      pendingUpload: true,
      fileName: att.fileName,
      fileType: att.fileType,
      fileSize: att.fileSize,
      fileUrl: att.localFileId || att.id,
      ...(att.durationSeconds != null ? { durationSeconds: att.durationSeconds } : {}),
      ...(Array.isArray(att.vaultContents) && att.vaultContents.length
        ? { vaultContents: att.vaultContents }
        : {}),
    };
  });

export const resolveAttachmentsForSend = async (attachments = []) => {
  if (!attachments?.length) return attachments;
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  const resolved = [];
  for (const att of attachments) {
    if (!isPendingLocalAttachment(att)) {
      resolved.push(att);
      continue;
    }
    const stored = await readLocalFile(att.localFileId || att.id);
    if (!stored?.blob) {
      throw Object.assign(new Error('Offline file is missing'), { code: 'ERR_FILE_MISSING' });
    }
    const blob = stored.blob;
    const file = blob instanceof File
      ? blob
      : new File([blob], att.fileName || stored.name || 'file', { type: stored.type || blob.type || '' });
    const results = await uploadFilesWithProgress({
      apiUrl: API_URL,
      token,
      files: [{ file, fileType: att.fileType }],
    });
    const data = results[0]?.data || {};
    resolved.push({
      ...data,
      fileType: inferAttachmentFileType({ ...data, fileType: att.fileType || data.fileType }),
      ...(att.durationSeconds != null ? { durationSeconds: att.durationSeconds } : {}),
    });
    await deleteLocalFile(att.localFileId || att.id);
  }
  return resolved;
};
